import { DoughBatch, CreateDoughBatchRequest, UpdateDoughBatchRequest, DoughProductName, DoughRecipe, DoughRecipeIngredient } from '../types/doughBatch';
import { DoughBatchIngredient } from '../types/doughBatchIngredient';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export interface CreateDoughBatchResponse {
  doughBatch: DoughBatch;
  ingredients: DoughBatchIngredient[];
}

export interface FetchDoughRecipesResponse {
  recipes: DoughRecipe[];
}

export interface FetchDoughRecipeResponse {
  ingredients: DoughRecipeIngredient[];
}

export const doughBatchApiService = {
  /**
   * Fetch all dough batches from the backend
   */
  fetchDoughBatches: async (): Promise<DoughBatch[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batches`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch dough batches');
      }

      return data.doughBatches;
    } catch (error) {
      console.error('Error fetching dough batches from API:', error);
      throw error;
    }
  },

  /**
   * Fetch a single dough batch by ID
   */
  fetchDoughBatchById: async (id: string): Promise<DoughBatch> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batches/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch dough batch');
      }

      return data.doughBatch;
    } catch (error) {
      console.error('Error fetching dough batch by ID from API:', error);
      throw error;
    }
  },

  /**
   * Get the next batch number for preview
   */
  getNextBatchNumber: async (productName: DoughProductName, dateOfProduction: string): Promise<string> => {
    try {
      const params = new URLSearchParams({ productName, dateOfProduction });
      const response = await fetch(`${API_BASE_URL}/dough-batches/next-batch-number?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get next batch number');
      }

      return data.batchNumber;
    } catch (error) {
      console.error('Error getting next batch number from API:', error);
      throw error;
    }
  },

  /**
   * Create a new dough batch (returns batch with auto-generated ingredients)
   */
  createDoughBatch: async (batch: CreateDoughBatchRequest): Promise<CreateDoughBatchResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(batch)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create dough batch');
      }

      return {
        doughBatch: data.doughBatch,
        ingredients: data.ingredients || []
      };
    } catch (error) {
      console.error('Error creating dough batch:', error);
      throw error;
    }
  },

  /**
   * Update an existing dough batch
   */
  updateDoughBatch: async (id: string, batch: UpdateDoughBatchRequest): Promise<DoughBatch> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batches/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(batch)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update dough batch');
      }

      return data.doughBatch;
    } catch (error) {
      console.error('Error updating dough batch:', error);
      throw error;
    }
  },

  /**
   * Delete a dough batch by ID
   */
  deleteDoughBatch: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batches/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete dough batch');
      }
    } catch (error) {
      console.error('Error deleting dough batch:', error);
      throw error;
    }
  },

  /**
   * Fetch all dough recipes (for product dropdown)
   */
  fetchDoughRecipes: async (): Promise<DoughRecipe[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batches/recipes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch dough recipes');
      }

      return data.recipes;
    } catch (error) {
      console.error('Error fetching dough recipes from API:', error);
      throw error;
    }
  },

  /**
   * Fetch recipe ingredients for a specific product
   */
  fetchDoughRecipe: async (productName: DoughProductName): Promise<DoughRecipeIngredient[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batches/recipes/${encodeURIComponent(productName)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch dough recipe');
      }

      return data.ingredients;
    } catch (error) {
      console.error('Error fetching dough recipe from API:', error);
      throw error;
    }
  }
};
